import React from "react";
import { money } from "../../lib/api";
import { computeProductPrice, shippingCents, firstOrderDiscountCents, FIRST_ORDER_PCT } from "../../lib/pricing";
import { useT } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Apple, CreditCard, Loader2, ShieldCheck } from "lucide-react";

export default function PaymentStep({ draft, settings, onPay, paying, zelleEnabled }) {
  const { t } = useT();
  const pricing = computeProductPrice(settings, draft.kit_qty);
  const subtotal = pricing ? pricing.product_price_cents : 0;
  const sh = settings?.shipping || {};
  const shipping = shippingCents(settings, draft.kit_qty, subtotal);
  const discountActive = !!(draft.discount && draft.discount.type === "first_order");
  const discountCents = firstOrderDiscountCents(subtotal, discountActive);
  const addons = settings?.add_ons || [];
  const addonLines = addons
    .map((a) => ({ ...a, qty: Number((draft.add_ons || {})[a.id] || 0) }))
    .filter((a) => a.qty > 0)
    .map((a) => ({ title: a.title, qty: a.qty, total: a.qty * Number(a.price_cents || 0) }));
  const addonsCents = addonLines.reduce((s, a) => s + a.total, 0);
  const total = subtotal - discountCents + shipping + addonsCents;

  return (
    <div>
      <div className="rounded-xl border bg-white p-4">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">{t("subtotal")}</dt><dd className="tabular-nums">{money(subtotal)}</dd></div>
          {discountCents > 0 && (
            <div className="flex justify-between" data-testid="summary-discount-line"><dt style={{ color: "hsl(var(--success))" }}>{t("first_order_discount_label", { pct: FIRST_ORDER_PCT })}</dt><dd className="tabular-nums" style={{ color: "hsl(var(--success))" }}>−{money(discountCents)}</dd></div>
          )}
          {addonLines.map((a) => (
            <div key={a.title} className="flex justify-between" data-testid="summary-addon-line"><dt className="text-[hsl(var(--muted-foreground))]">{a.title} ×{a.qty}</dt><dd className="tabular-nums">{money(a.total)}</dd></div>
          ))}
          <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">{t("shipping")}</dt><dd className="tabular-nums">{shipping === 0 ? t("ship_free") : money(shipping)}</dd></div>
          {shipping > 0 && <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{sh.note || "Estimated shipping until UPS is connected."}</p>}
          <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">{t("tax")}</dt><dd>To be confirmed</dd></div>
          <Separator className="my-1" />
          <div className="flex justify-between text-base font-semibold"><dt>{t("total")}</dt><dd className="tabular-nums" data-testid="order-total">{money(total)}</dd></div>
        </dl>
      </div>

      <div className="mt-5 space-y-3">
        <Button onClick={() => onPay("stripe")} disabled={paying} data-testid="pay-apple-button"
          className="h-14 w-full rounded-xl bg-black text-base text-white hover:bg-black/90">
          {paying ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Apple className="mr-2 h-5 w-5" />} {t("apple_pay")}
        </Button>
        <p className="-mt-1 text-center text-[11px] text-[hsl(var(--muted-foreground))]">{t("apple_note")}</p>

        <Button onClick={() => onPay("stripe")} disabled={paying} variant="outline" data-testid="pay-card-button"
          className="h-12 w-full rounded-xl border bg-white text-base">
          <CreditCard className="mr-2 h-5 w-5" /> {t("pay_card")}
        </Button>

        {zelleEnabled && (
          <>
            <Separator className="my-2" />
            <Button onClick={() => onPay("zelle")} disabled={paying} variant="ghost" data-testid="pay-zelle-button"
              className="h-11 w-full rounded-xl border border-dashed bg-white text-sm">
              {t("pay_zelle")}
            </Button>
            <p className="text-center text-[11px] text-[hsl(var(--muted-foreground))]">{t("zelle_note")}</p>
          </>
        )}
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: "hsl(var(--success))" }} /> {t("secure_note")}
      </p>
    </div>
  );
}
